import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    ReplaySubject,
    Subject,
    switchMap
} from 'rxjs';
import { CountryCodePipe } from '../../pipes/country-code.pipe';
import { SbCompanyLogoComponent } from '../sb-company-logo/sb-company-logo.component';
import { CamelToSentenceCasePipe } from '../../pipes/camel-to-sentence-case.pipe';
import { FormatCurrencyPipe } from '../../pipes/format-currency.pipe';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SbAnimatedNumberComponent } from '../sb-animated-number/sb-animated-number.component';
import { AcctsQuery } from '../../state/accts/accts.query';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { transformAcctToAccount } from '../../state/accts/accts.store';
import {
    Badge,
    BadgesChangeEvent,
    SbBadgesComponent
} from '../sb-badges/sb-badges.component';
import {
    FormBuilder,
    FormControl,
    FormsModule,
    ReactiveFormsModule
} from '@angular/forms';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { PageEvent } from '@angular/material/paginator';
import { InferFilter } from '../../state/query-filter.functions';
import { AccountTag, AccountTypeEnum, Acct } from '../../state/accts/acct.model';
import {
    fadeInOut,
    fadeInOutModal,
    list,
    slideInOut
} from '../../animations/fade-in-out.animation';
import { AccountIssuersQuery } from '../../../pages/admin/state/account-issuers/account-issuers.query';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import { transformAccountIssuerToIssuer } from '../../../pages/admin/state/account-issuers/account-issuers.store';
import { HighlightPipe } from '../../../features/equity-search/search.highlight.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PositionsQuery } from '../../../features/positions/state/positions.query';
import { PositionsService } from '../../../features/positions/state/positions.service';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { get } from 'dot-prop';
import { UiQuery } from '../../state/ui/ui.query';
import { startOfYear, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { Period } from '../../utils/chart-utils';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { transformOwnerToLegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity-store.service';
import { AcctsService } from '../../state/accts/accts.service';
import { IndicatorState } from '../sb-pending-button-indicator/sb-pending-button-indicator.component';
import { distinctUntilArrayItemChanged, HashMap, isArray } from '@datorama/akita';
import { UniquePipe } from '../../pipes/unique.pipe';
import { TagsQuery } from 'src/app/features/new-tags/state/tags.query';
import { NewTag } from '../../../features/new-tags/state/tags.model';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { AccountIdConcatPipe } from './account-id-concat.pipe';
import { SbNotificationBadgeComponent } from '../sb-notification-badge/sb-notification-badge.component';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { SbGenericTypeSelectComponent } from '../../../pages/authenticated/accounting/components/sb-generic-type-select/sb-generic-type-select.component';
import { SanitizePipe } from '../../pipes/sanitize.pipe';
import { ComponentSettings } from '../../../features/user-settings/state/user-setting.model';
import { Instrument } from '../../state/instruments/instrument.model';
import { UserSettingsService } from '../../../features/user-settings/state/user-settings.service';
import { SbFilterCountPipe } from '../../pipes/sb-filter-count.pipe';
import { InstrumentsQuery } from '../../state/instruments/instruments.query';

@Component({
    selector: 'sb-account-list',
    standalone: true,
    imports: [
        CommonModule,
        CountryCodePipe,
        SbCompanyLogoComponent,
        CamelToSentenceCasePipe,
        FormatCurrencyPipe,
        MatMenuModule,
        TablerIconsModule,
        RouterLink,
        SbAnimatedNumberComponent,
        SbBadgesComponent,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatPaginatorModule,
        HighlightPipe,
        TranslateModule,
        MatTooltipModule,
        UniquePipe,
        MatCheckboxModule,
        AccountIdConcatPipe,
        SbNotificationBadgeComponent,
        SbGenericTypeSelectComponent,
        SanitizePipe,
        SbFilterCountPipe
    ],
    templateUrl: './sb-account-list.component.html',
    styleUrls: ['./sb-account-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [list, slideInOut, fadeInOut, fadeInOutModal]
})
export class SbAccountListComponent implements OnInit, OnDestroy {
    get isLoading$(): Observable<boolean> {
        return this._isLoading$.asObservable();
    }

    set isLoading(value: boolean) {
        this._isLoading$.next(value);
    }
    get isStandalone(): boolean {
        return this._isStandalone;
    }

    @Input() set isStandalone(value: boolean) {
        this._isStandalone = value;
    }
    get useShortNumberFormat(): boolean {
        return this._useShortNumberFormat;
    }

    set useShortNumberFormat(value: boolean) {
        this._useShortNumberFormat = value;
    }
    get balanceKey$(): Observable<string> {
        return this._balanceKey$.asObservable();
    }

    get balanceKey(): string {
        return this._balanceKey$.getValue();
    }

    set balanceKey(value: string) {
        this._balanceKey$.next(value);
    }
    _unsubscribe$ = new Subject();
    get currentPage$(): Observable<number> {
        return this._currentPage$.asObservable();
    }

    set currentPage(value: number) {
        this._currentPage$.next(value);
    }
    get showFilters(): boolean {
        return this._showFilters;
    }

    set showFilters(value: boolean) {
        this._showFilters = value;
    }
    constructor(
        private _accountsService: AcctsService,
        private _accountsQuery: AcctsQuery,
        private _issuerQuery: AccountIssuersQuery,
        private _instrumentsQuery: InstrumentsQuery,
        private _legalEntityQuery: LegalEntityQuery,
        private _positionsQuery: PositionsQuery,
        private _positionsService: PositionsService,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService,
        private _translateService: TranslateService,
        private _tagsQuery: TagsQuery,
        private _fb: FormBuilder,
        private _router: Router,
        private _route: ActivatedRoute,
        private _routerQuery: RouterQuery,
        public ui: UiQuery
    ) {}

    viewPortSize$ = this.ui.viewportSize$;

    ngOnInit() {
        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ACCOUNT_TYPE_FILTER',
                'sb-account-list'
            ),
            this._accountsQuery.selectAvailableAccountTypes$.pipe(
                filter((types) => types.length > 0)
            )
        ])
            .pipe(
                //debounceTime(0),
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.filterForm.controls.type.setValue(persisted);
                    } else {
                        this.filterForm.controls.type.setValue(available);
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'LEGAL_ENTITY_FILTER',
                'sb-account-list'
            ),
            this.availableLegalEntities$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.legalEntitiesCtrl.setValue(persisted);
                    } else {
                        this.legalEntitiesCtrl.setValue(
                            available.map((legalEntity) => legalEntity.data.ownerId)
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ISSUER_FILTER',
                'sb-account-list'
            ),
            this.availableIssuers$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.issuersCtrl.setValue(persisted);
                    } else {
                        this.issuersCtrl.setValue(
                            available.map((issuer) => issuer.data.accountIssuerId)
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'INSTRUMENT_FILTER',
                'sb-account-list'
            ),
            this.availableInstruments$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.instrumentsCtrl.setValue(persisted);
                    } else {
                        this.instrumentsCtrl.setValue(
                            available.reduce((acc, instrumentBadge) => {
                                return [...acc, ...instrumentBadge.data];
                            }, [])
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        // not needed anymore as the positions query will auto update
        /*combineLatest([
            this._accountsQuery
                .selectAccountsByFilter$({
                    accountType: {
                        ne: AccountTypeEnum.BROKER
                    }
                })
                .pipe(
                    filter((accounts) => accounts.length > 0),
                    take(1),
                    map((accounts) => accounts.map((account) => account.accountId))
                ),
            this.baseCurrency$
        ])
            .pipe(
                switchMap(([accountIds, baseCurrency]) => {
                    this.loadingBalance$.next(true);
                    return this._positionsService
                        .get({ accountIds, includeNetted: false, baseCurrency })
                        .pipe(tap((_) => this.loadingBalance$.next(false)));
                }),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();
            */

        this._accountsQuery.selectAccountTags$
            .pipe(
                tap((tags) => {
                    this.filterForm.controls.tags.setValue(tags);
                }),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();
    }

    baseCurrency$ = this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY');
    maxAccountCloseDate$ = of(new Date());
    periods: Array<Period> = [
        {
            name: '1W',
            start: subWeeks(new Date(), 1),
            end: new Date()
        },
        {
            name: '1M',
            start: subMonths(new Date(), 1),
            end: new Date()
        },
        {
            name: '3M',
            start: subMonths(new Date(), 3),
            end: new Date()
        },
        {
            name: 'YTD',
            start: subDays(startOfYear(new Date()), 1),
            end: new Date()
        },
        {
            name: '1Y',
            start: subYears(new Date(), 1),
            end: new Date()
        },
        {
            name: '3Y',
            start: subYears(new Date(), 3),
            end: new Date()
        },
        {
            name: '5Y',
            start: subYears(new Date(), 5),
            end: new Date()
        },
        {
            name: 'All',
            start: null,
            end: null
        }
        /*{
            name: 'Custom',
            start: null,
            end: null
        }*/
    ];
    period$ = new BehaviorSubject({
        ...this.periods[1]
    });

    setPeriod(period: Period) {
        this.period$.next({ ...period });
    }
    private _currentPage$ = new BehaviorSubject(1);
    tags$ = this._accountsQuery.selectAccountTags$.pipe(
        tap((tags) => {
            const existingTags = this.filterForm.value.tags;
            this.filterForm.controls.tags.setValue(tags);
        }),
        map((tags) =>
            tags.map((tag): Badge<NewTag> => {
                return {
                    title: tag.title,
                    outline:
                        !this.filterForm.value.tags.find((tag) => tag.title) ?? false,
                    data: tag
                };
            })
        ),
        shareReplay(1)
    );

    legalEntitiesCtrl = new FormControl<Array<LegalEntity['ownerId']>>([]);
    activeLegalEntityIds$ = this._legalEntityQuery.active$.pipe(
        map((legalEntities) => legalEntities.map((legalEntity) => legalEntity.ownerId)),
        distinctUntilArrayItemChanged(),
        tap((_) => {
            this.currentPage = 1;
        })
    ); /*this.legalEntitiesCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.legalEntitiesCtrl.value),
        distinctUntilArrayItemChanged(),
        tap((_) => {
            this.currentPage = 1;
        }),
        shareReplay(1)
    );*/

    issuersCtrl = new FormControl<Array<AccountIssuer['accountIssuerId']>>([]);
    activeIssuerIds$ = this.issuersCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.issuersCtrl.value),
        distinctUntilArrayItemChanged(),
        tap((_) => {
            this.currentPage = 1;
        }),
        shareReplay(1)
    );

    availableIssuers$ = combineLatest([
        this._accountsQuery.selectAvailableAccountIssuers$,
        this._issuerQuery.allAsObject$.pipe(
            filter((issuers) => Object.keys(issuers).length > 0)
        ),
        this.activeIssuerIds$
    ]).pipe(
        map(([issuerIds, issuers, activeIssuerIds]) => {
            const currentSelection = [...activeIssuerIds];
            return issuerIds.map((id) => {
                // if issuer is not found in the list of issuers then use a default issuer object
                const issuer = issuers[id] || {
                    displayName: 'Unknown',
                    accountIssuerShortName: 'Unknown',
                    accountIssuerLongName: 'Unknown',
                    accountIssuerId: null
                };
                return {
                    title:
                        issuer.displayName ||
                        issuer.accountIssuerShortName ||
                        issuer.accountIssuerLongName,
                    outline:
                        activeIssuerIds.length === 0
                            ? false
                            : !currentSelection.includes(issuer.accountIssuerId),
                    data: issuer
                };
            });
        }),
        shareReplay(1)
    );

    instrumentsCtrl = new FormControl<Array<Instrument['instrumentId']>>([]);
    activeInstrumentIds$ = this.instrumentsCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.instrumentsCtrl.value),
        distinctUntilArrayItemChanged(),
        tap((_) => {
            this.currentPage = 1;
        }),
        shareReplay(1)
    );

    availableInstruments$ = combineLatest([
        this._positionsQuery.selectInstrumentIdsWithPositions$(),
        this._instrumentsQuery.allAsObject$.pipe(
            filter((instruments) => Object.keys(instruments).length > 0)
        ),
        this.activeInstrumentIds$
    ]).pipe(
        map(([instrumentIds, instrumentsMap, activeInstrumentIds]) => {
            const currentSelection = [...activeInstrumentIds];
            // each instrument should get a unique tag based on the instrument name, some instrument don't have meaningful names so we use the type as a fallback to group them instead eg. cash instruments.
            return Object.values(
                instrumentIds.reduce(
                    (
                        acc: HashMap<{
                            title: string;
                            outline: boolean;
                            data: Array<Instrument['instrumentId']>;
                        }>,
                        instrumentId
                    ) => {
                        const instrument = instrumentsMap[instrumentId];
                        const name =
                            instrument?.decoratedInstrumentName ||
                            instrument?.name ||
                            instrument?.type ||
                            'Unknown';
                        if (!acc[name]) {
                            acc[name] = {
                                title: name,
                                outline:
                                    activeInstrumentIds.length === 0
                                        ? false
                                        : !currentSelection.includes(
                                              instrument?.instrumentId || instrumentId
                                          ),
                                data: [instrument?.instrumentId || instrumentId]
                            };
                        } else {
                            acc[name].data.push(instrument?.instrumentId || instrumentId);
                        }
                        return acc;
                    },
                    {}
                )
            ).sort((a, b) => a.title.localeCompare(b.title));
        }),
        shareReplay(1)
    );

    availableInstrumentIds$ = this.availableInstruments$.pipe(
        map((instruments) =>
            instruments.reduce((acc, instrument) => [...acc, ...instrument.data], [])
        ),
        shareReplay(1)
    );

    availableAccountTypes$ = combineLatest([
        this._accountsQuery.selectAvailableAccountTypes$,
        this._translateService.currentLoader.getTranslation('en')
    ]).pipe(
        map(([types, translations]) => {
            const currentSelection = this.filterForm.value.type;
            return types.map(
                (type): Badge => ({
                    title: translations[type] || type || 'Not set', // fallback to type if translation is not available
                    outline: !currentSelection.includes(type),
                    data: type
                })
            );
        })
    );

    filterAccountTypes($event: BadgesChangeEvent<AccountTypeEnum>) {
        if ($event.type === 'toggle-active') {
            this.filterForm.controls.type.setValue(
                $event.badges.filter((badge) => !badge.outline).map((badge) => badge.data)
            );

            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'ACCOUNT_TYPE_FILTER',
                filter: allSelected ? [] : this.filterForm.controls.type.value
            });
        }
    }

    availableLegalEntities$ = combineLatest([
        this._accountsQuery.selectAvailableLegalEntities$,
        this._legalEntityQuery.allAsObject$
    ]).pipe(
        map(([ownerIds, owners]) => {
            const currentSelection = this.legalEntitiesCtrl.value;
            return ownerIds.map((id) => {
                // if legal entity is not found in the list of legal entities then use a default legal entity object
                const legalEntity = owners[id] || {
                    displayName: 'Unknown',
                    shortName: 'Unknown',
                    longName: 'Unknown',
                    ownerId: null
                };
                return {
                    title:
                        legalEntity.displayName ||
                        legalEntity.shortName ||
                        legalEntity.longName,
                    outline: !currentSelection.includes(legalEntity.ownerId),
                    data: legalEntity
                };
            });
        })
    );

    private _showFilters = false;

    groupByOptions = [
        { value: 'id', label: 'Account' },
        { value: 'issuer', label: 'Issuer' },
        { value: 'legalEntity', label: 'Legal entity' }
        //{ value: 'tags', label: 'Tags' }
    ];

    get groupBy() {
        return { key: this.groupByCtrl.value.value, name: this.groupByCtrl.value.label };
    }

    groupByCtrl = new FormControl(
        this._routerQuery.getQueryParams('groupBy')
            ? this.groupByOptions.find(
                  (option) => option.value === this._routerQuery.getQueryParams('groupBy')
              )
            : this.groupByOptions[0]
    );

    groupBy$ = this.groupByCtrl.valueChanges.pipe(
        tap((value) => {
            this.currentPage = 1;
            const queryParams = { groupBy: value.value };
            this._router.navigate([], {
                relativeTo: this._route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }),
        startWith(this.groupByCtrl.value),
        map((group) => ({
            key: group.value,
            name: group.label
        })),
        distinctUntilChanged(),
        shareReplay(1)
    );

    sortForm = this._fb.group({
        sortBy: ['name'],
        sortByOrder: ['asc']
    });
    sortByOptions = [
        { key: 'balanceInBaseCurrency', name: 'Balance' },
        { key: 'name', name: 'Name' },
        { key: 'issuer.name', name: 'Issuer' }
        //{ key: 'performance', name: 'Performance' }
    ];

    sorting$ = this.sortForm.valueChanges.pipe(
        startWith(this.sortForm.value),
        distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
    );
    filterForm = this._fb.group({
        search: [''],
        type: new FormControl<Array<Acct['accountType']>>([]),
        tags: new FormControl<Array<NewTag>>([])
    });
    accountFilter$ = this.filterForm.valueChanges.pipe(
        debounceTime(200),
        startWith(this.filterForm.value),
        distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        tap((value) => {
            this.currentPage = 1;
        }),
        shareReplay(1)
    );
    private _filteredAccounts$ = combineLatest([
        this.accountFilter$,
        this.activeLegalEntityIds$,
        this.activeIssuerIds$,
        this.maxAccountCloseDate$,
        this.groupBy$
    ]).pipe(
        map(
            ([
                formValues,
                activeLegalEntities,
                activeIssuerIds,
                maxAccountCloseDate,
                groupBy
            ]): InferFilter<Acct> => {
                const accountType = {
                    in: formValues.type,
                    ne: AccountTypeEnum.BROKER
                };
                const parentAccountId = { fn: (parentAccountId) => !parentAccountId }; // don't show sub-accounts
                // if not group by account id...
                if (groupBy.key !== 'id') {
                    // ...then exclude portfolios and include sub-accounts in view
                    accountType['nin'] = [AccountTypeEnum.PORTFOLIO];
                    delete parentAccountId.fn; // show sub-accounts
                }

                let filter: InferFilter<Acct> = {
                    accountType,
                    parentAccountId,
                    closingDate: {
                        fn: (closingDate: string) => {
                            return closingDate
                                ? new Date(closingDate) > maxAccountCloseDate
                                : true;
                        }
                    },
                    tags: {
                        // @ts-ignore
                        fn: (accountTags: HashMap<NewTag>) => {
                            // if tags are added to an account...
                            return Object.keys(accountTags || {}).length > 0
                                ? // ...then check if the account has any of the selected tags, return account if it has at least one of the selected tags
                                  Object.keys(accountTags).some((tag) =>
                                      formValues.tags.map((tag) => tag.id).includes(tag)
                                  )
                                : // ...if no tags are added to the account then return the account anyway
                                  true;
                        }
                    }
                };
                if (activeIssuerIds.length > 0) {
                    filter.accountIssuerId = {
                        in: activeIssuerIds
                    };
                }
                // Always filter on legal entities, i.e. if none are selected no positions will be shown
                //if (activeLegalEntities.length > 0) {
                filter.ownerId = {
                    in: activeLegalEntities
                };
                //}
                // @ts-ignore
                return filter;
            }
        ),
        switchMap((accountFilter) =>
            combineLatest([
                this._accountsQuery.selectAccountsByFilter$(accountFilter),
                this._issuerQuery.selectLoading().pipe(
                    filter((loading) => loading === false),
                    take(1),
                    switchMap((_) => this._issuerQuery.allAsObject$)
                ),
                this._legalEntityQuery.selectLoading().pipe(
                    filter((loading) => loading === false),
                    take(1),
                    switchMap((_) => this._legalEntityQuery.allAsObject$)
                ),
                this._tagsQuery.allAsObject$
            ])
        ),
        withLatestFrom(this.accountFilter$),
        map(([[accounts, issuers, legalEntities, tags], filters]) => {
            const filteredAccounts: Array<AccountItem> = [];
            for (let i = 0; i < accounts.length; i++) {
                const account = accounts[i];
                const accountItem = getAccountItem(
                    account,
                    issuers[account.accountIssuerId],
                    legalEntities[account.ownerId],
                    tags as any
                );
                const searchText =
                    `${accountItem.displayName}#${accountItem.number}#${accountItem.type}#${accountItem.issuer?.displayName}#${accountItem.legalEntity?.displayName}`.toLowerCase();
                if (searchText.includes(filters.search?.toLowerCase())) {
                    filteredAccounts.push(accountItem);
                }
            }
            return filteredAccounts;
        })
    );
    _balances$ = combineLatest([
        this.groupBy$.pipe(
            map((groupBy) => groupBy.key === 'id'),
            distinctUntilChanged()
        ),
        this.activeInstrumentIds$
    ]).pipe(
        switchMap(([groupOnParent, instrumentIds]) => {
            const filter =
                instrumentIds.length > 0
                    ? {
                          instrument: {
                              instrumentId: {
                                  in: instrumentIds
                              }
                          }
                      }
                    : {};

            return this._positionsQuery.selectAllByAccount$(groupOnParent, filter);
        })
    );

    private _sortedChunkedAccounts$ = combineLatest([
        this._filteredAccounts$,
        this._balances$,
        this.sorting$,
        this.groupBy$
    ]).pipe(
        map(([accounts, positions, sorting, groupBy]) => {
            const accountsWithBalances = accounts.reduce((acc, account) => {
                const position = positions[account.id];
                let thumbnail = {
                    name: account.name,
                    displayName: account.displayName,
                    type: 'account',
                    website:
                        account[groupBy.key] && account[groupBy.key]['website']
                            ? account[groupBy.key]['website']
                            : groupBy.key === 'id'
                            ? account.issuer?.website
                            : null
                };
                if (!thumbnail.website) {
                    let icon = null;
                    if (groupBy.key === 'legalEntity') {
                        icon = account.legalEntity.type;
                    } else if (groupBy.key === 'tags') {
                        icon = 'TAG';
                    } else {
                        icon = account.type;
                    }
                    thumbnail = { ...thumbnail, icon } as typeof thumbnail & {
                        icon: string;
                    };
                }
                // add account if hideIfZeroBalance is false or if hideIfZeroBalance is true and balance is not zero
                if (
                    !(
                        account.hideIfZeroBalance &&
                        (!position || Math.abs(position?.balanceInBaseCurrency) <= 0.001)
                    )
                ) {
                    acc.push({
                        ...account,
                        thumbnail,
                        balance: position?.balance ?? 0,
                        balanceInBaseCurrency: position?.balanceInBaseCurrency ?? 0,
                        data: position?.subAccounts ?? []
                    });
                }
                return acc;
            }, []);
            let weightedAccounts;
            // if not grouped by account id...
            if (groupBy.key !== 'id') {
                const groupedAccounts = accountsWithBalances.reduce((acc, account) => {
                    const accountGroupProperty = get<any>(account, groupBy.key);
                    let groupKeys = [];
                    if (
                        isArray(accountGroupProperty) &&
                        groupBy.key === 'tags' &&
                        accountGroupProperty.length > 0
                    ) {
                        groupKeys = (accountGroupProperty as Array<AccountTag>).map(
                            (accountTag) => accountTag.title
                        );
                    } else {
                        groupKeys = [
                            accountGroupProperty?.id ||
                                (groupBy.key === 'tags' ? 'Not tagged' : 'Unknown')
                        ];
                    }
                    groupKeys.forEach((key) => {
                        if (!acc[key]) {
                            const name =
                                groupBy.key === 'tags'
                                    ? key
                                    : get<any>(account, groupBy.key)?.displayName ||
                                      'Unknown';
                            acc[key] = {
                                name,
                                id: key,
                                data: [],
                                balance: 0,
                                balanceInBaseCurrency: 0,
                                absolutBalance: 0,
                                thumbnail: account.thumbnail
                            };
                        }
                        acc[key].data.push(account);
                        acc[key].balance += account.balance;
                        acc[key].balanceInBaseCurrency += account.balanceInBaseCurrency;
                        acc[key].absolutBalance += Math.abs(
                            account.balanceInBaseCurrency
                        );
                    });
                    return acc;
                }, {});

                weightedAccounts = Object.entries<any>(groupedAccounts).map(
                    ([key, value]) => {
                        const performance = {
                            '1W': 0,
                            '1M': 0,
                            '3M': 0,
                            YTD: 0,
                            '1Y': 0,
                            '3Y': 0,
                            '5Y': 0,
                            All: 0
                        };
                        const data = value.data.map((account) => {
                            const weight =
                                Math.abs(account.balanceInBaseCurrency) /
                                value.absolutBalance;
                            return {
                                ...account,
                                weight,
                                weightedPerformance: Object.entries<number>(
                                    account.performance
                                ).reduce((acc, [key, value]) => {
                                    performance[key] += value * weight;
                                    acc[key] = value * weight;
                                    return acc;
                                }, {})
                            };
                        });
                        return {
                            ...value,
                            data,
                            performance,
                            key,
                            thumbnail: value?.thumbnail,
                            id: value?.id
                        };
                    }
                );
            }

            const sortedAccounts = [...(weightedAccounts || accountsWithBalances)].sort(
                (a, b) => {
                    if (sorting.sortByOrder === 'asc') {
                        return (get<string>(a, sorting.sortBy) + '').localeCompare(
                            get<string>(b, sorting.sortBy) + '',
                            'en',
                            { numeric: true }
                        );
                    } else {
                        return (get<string>(b, sorting.sortBy) + '').localeCompare(
                            get<string>(a, sorting.sortBy) + '',
                            'en',
                            { numeric: true }
                        );
                    }
                }
            );
            const maxSizePerBatch: number = 8;
            const chunk = <T>(array: Array<T>, size: number) =>
                Array.from({ length: Math.ceil(array.length / size) }, (value, index) =>
                    array.slice(index * size, index * size + size)
                ) as Array<Array<T>>;
            const data = chunk<AccountItem>(sortedAccounts, maxSizePerBatch);
            return { data, pages: data.length, total: sortedAccounts.length };
        })
    );

    private _balanceKey$ = new BehaviorSubject('balanceInBaseCurrency');
    useBaseCurrency$ = this._balanceKey$.pipe(
        map((key) => key === 'balanceInBaseCurrency'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    accounts$ = combineLatest([this._sortedChunkedAccounts$, this._currentPage$]).pipe(
        map(([accounts, currentPage]) => {
            return {
                accounts: accounts?.data[currentPage - 1] as Array<
                    AccountItem & { thumbnail: any; data?: Array<AccountItem> }
                >,
                pages: accounts.pages,
                total: accounts.total
            };
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap((accounts) => (this.isLoading = accounts.accounts === undefined)),
        startWith({ accounts: [], pages: 0, total: 0 }),
        shareReplay(1)
    );
    viewCtrl = new FormControl('condensed');
    viewOptions = [
        {
            value: 'detailed',
            name: 'Detailed'
        },
        {
            value: 'condensed',
            name: 'Condensed'
        },
        {
            value: 'minimal',
            name: 'Minimal'
        }
    ];
    private _useShortNumberFormat: boolean = false;
    loadingBalance$ = this._positionsQuery.isLoading$;

    onPageChange($event: PageEvent) {
        this.currentPage = $event.pageIndex + 1;
    }

    trackByFn(index, item) {
        return item.id + item.name;
    }
    toggleShowFilters() {
        this.showFilters = !this.showFilters;
    }

    toggleCurrencyDisplay() {
        this.balanceKey =
            this.balanceKey === 'balance' ? 'balanceInBaseCurrency' : 'balance';
    }

    ngOnDestroy() {
        this._unsubscribe$.next(true);
        this._unsubscribe$.complete();
    }

    toggleSortOrder() {
        const currentSortOrder = this.sortForm.controls.sortByOrder.value;
        this.sortForm.controls.sortByOrder.setValue(
            currentSortOrder === 'asc' ? 'desc' : 'asc'
        );
    }

    private _isStandalone = true;
    addTagState$ = new ReplaySubject<IndicatorState>(0);

    updateTags($event: BadgesChangeEvent<NewTag>, account: AccountItem) {
        // if event does not have a badge, then we do not need to do anything
        if (!$event.badge) {
            return;
        }
        this.addTagState$.next('loading');
        if ($event.type === 'add') {
            this._accountsService.addTag(account.id, $event.badge.title).subscribe({
                next: (tag) => {
                    this.addTagState$.next('success');
                }
            });
        }

        if ($event.type === 'delete') {
            this._accountsService.removeTag(account.id, $event.badge['id']).subscribe({
                next: (tag) => {
                    this.addTagState$.next('success');
                }
            });
        }
    }

    filterTags($event: BadgesChangeEvent) {
        if ($event.type === 'toggle-active') {
            this.filterForm.controls.tags.setValue(
                $event.badges.filter((badge) => !badge.outline).map((badge) => badge.data)
            );
        }
    }
    /* TODO: remove once global filter for legal entities is implemented and released
    filterLegalEntities($event: BadgesChangeEvent<LegalEntity>) {
        if ($event.type === 'toggle-active') {
            this.legalEntitiesCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.ownerId)
            );
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'LEGAL_ENTITY_FILTER',
                filter: allSelected ? [] : this.legalEntitiesCtrl.value
            });
        }
    }*/

    filterIssuers($event: BadgesChangeEvent<AccountIssuer>) {
        if ($event.type === 'toggle-active') {
            this.issuersCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.accountIssuerId)
            );
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'ISSUER_FILTER',
                filter: allSelected ? [] : this.issuersCtrl.value
            });
        }
    }

    filterInstruments($event: BadgesChangeEvent<Array<Instrument['instrumentId']>>) {
        if ($event.type === 'toggle-active') {
            this.instrumentsCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .reduce((acc, badge) => {
                        return [...acc, ...badge.data];
                    }, [])
            );
            // check if all instruments are selected
            const selected = $event.badges.filter((badge) => !badge.outline);
            const allSelected = selected.length === $event.badges.length;
            this.saveFilter({
                setting: 'INSTRUMENT_FILTER',
                filter: allSelected ? [] : this.instrumentsCtrl.value
            });
        }
    }

    saveFilter({
        setting,
        filter
    }: {
        setting: keyof ComponentSettings;
        filter:
            | Array<LegalEntity['ownerId']>
            | Array<Instrument['instrumentId']>
            | Array<Instrument['type']>
            | Array<Acct['accountType']>
            | Array<AccountIssuer['accountIssuerId']>;
    }) {
        this._userSettingsService
            .saveComponentSetting(setting, filter, 'sb-account-list', true)
            .subscribe();
    }

    groupByCompareFn = (o1, o2) => o1 && o2 && o1.key === o2.key;

    private _hasPendingAnimation$ = new BehaviorSubject(false);
    hasPendingAnimation$ = this._hasPendingAnimation$.asObservable();
    private _isLoading$ = new BehaviorSubject(true);
    onAnimationStart($event: any) {
        this._hasPendingAnimation$.next(true);
    }

    onAnimationDone($event: any) {
        this._hasPendingAnimation$.next(false);
    }
}

export type AccountItem = ReturnType<typeof getAccountItem>;
const getAccountItem = (
    account: Acct,
    accountIssuer: AccountIssuer,
    owner: LegalEntity,
    tags: HashMap<NewTag>
) => {
    const issuer = accountIssuer
        ? { ...transformAccountIssuerToIssuer(accountIssuer) }
        : null;
    const legalEntity = owner ? { ...transformOwnerToLegalEntity(owner) } : null;
    return {
        ...transformAcctToAccount(account),
        issuer,
        legalEntity,
        tags: Object.keys(account.tags || {}).map((tagId) => tags[tagId]),
        performance: {
            '1W': Math.random() * (1 - -1) + -1,
            '1M': Math.random() * (1 - -1) + -1,
            '3M': Math.random() * (1 - -1) + -1,
            YTD: Math.random() * (1 - -1) + -1,
            '1Y': Math.random() * (1 - -1) + -1,
            '3Y': Math.random() * (1 - -1) + -1,
            '5Y': Math.random() * (1 - -1) + -1,
            All: Math.random() * (1 - -1) + -1
        }
    };
};
